/*
 * This file is a collection of map-library-specific exports that are commonly used across the app.
 * It exists to make it easier to switch between map libraries without having to change imports in many files.
 */
import 'mapbox-gl/dist/mapbox-gl.css';

import {
  LngLat,
  LngLatBounds,
  MapMouseEvent as GLMapMouseEvent,
  GeoJSONFeature as MapGeoJSONFeature,
} from 'mapbox-gl';
import { MapRef, MarkerProps, useMap } from 'react-map-gl';

export { Map, LngLat, LngLatBounds, Marker } from 'mapbox-gl';
export type MapMouseEvent = GLMapMouseEvent & {
  features?: MapGeoJSONFeature[] | undefined;
};
export type {
  LngLatBoundsLike,
  FitBoundsOptions,
  PointLike,
  DataDrivenPropertyValueSpecification,
  FilterSpecification,
  ExpressionSpecification,
  GeoJSONFeature as MapGeoJSONFeature,
  MapLayerMouseEvent,
  MapLayerTouchEvent,
  Anchor as PositionAnchor,
  LayerSpecification,
  PaintSpecification,
  FillLayerSpecification,
  LineLayerSpecification,
  MapEventType,
  MapEventOf,
} from 'mapbox-gl';

export {
  Map as MapComponent,
  MapProvider,
  Marker as MarkerComponent,
  useMap,
  useControl,
  Layer,
  Source,
  ScaleControl,
  AttributionControl,
  Popup,
} from 'react-map-gl';
export type {
  LayerProps,
  SourceProps,
  ViewState,
  MarkerDragEvent,
  MapRef,
  ControlPosition,
  MapStyle,
  AnyLayer,
  AnySource,
} from 'react-map-gl';

export type MarkerClickEvent = Parameters<
  NonNullable<MarkerProps['onClick']>
>[0];

// Wrappers
export const LngLatBoundsFromLngLat = (lngLat: LngLat): LngLatBounds => {
  return new LngLatBounds(lngLat, lngLat);
};

/**
 * Returns the map instance regardless of context source:
 * - current: from direct parent-child relationship via Map
 * - default: from broader scope management via MapProvider
 * - mapId: a specific map instance by its ID
 *
 * This handles both component composition patterns: direct nesting or provider-based,
 * and also allows targeting a specific map instance by its ID.
 */
export const useMapInstance = (mapId?: string): MapRef | undefined => {
  const maps = useMap();

  if (mapId) {
    return maps[mapId];
  }

  return maps.current || maps.default;
};
